import type {ConnectionMutationVariables} from '@github-ui/optimistic-connection/pendingOperations'
import {CONNECTION_MUTATION_KEY_ROOT} from '@github-ui/optimistic-connection/pendingOperations'
import {useMutationState, useQueryClient} from '@github-ui/react-query'
import {useMemo, useState} from 'react'

export type IssueDeletionResult = {issueId: string; completionError?: Error}

export type IssueDeletionState = {
  /** TanStack's unique mutation identity; distinct from the issue's identity. */
  id: number
  issueId: string
  owner: string
  repository: string
  number: number
  status: 'pending' | 'success' | 'error'
  /** A failed DELETE; the optimistic removal is rolled back. */
  error?: Error
  /** DELETE succeeded, but subsequent reconciliation failed. The removal stays confirmed. */
  completionError?: Error
}

/**
 * Observe new issue deletions across unmounting viewers. Mount this in a stable host, before
 * opening a viewer. Existing mutations are excluded at mount so a reopened pane never handles
 * an earlier deletion again. The structural connection key is unchanged for optimistic data.
 */
export function useIssueDeletionState(): readonly IssueDeletionState[] {
  const queryClient = useQueryClient()
  const [initialMutationId] = useState(() =>
    queryClient
      .getMutationCache()
      .getAll()
      .reduce((maxId, mutation) => Math.max(maxId, mutation.mutationId), 0),
  )

  const mutations = useMutationState<IssueDeletionState | undefined>({
    filters: {mutationKey: [CONNECTION_MUTATION_KEY_ROOT]},
    select: mutation => {
      const identity = (mutation.state.variables as ConnectionMutationVariables | undefined)?.issueDeletion
      if (!identity) return undefined
      const {status, error, data} = mutation.state
      if (status === 'idle') return undefined
      const result = data as IssueDeletionResult | undefined
      return {
        id: mutation.mutationId,
        ...identity,
        status,
        error: status === 'error' && error instanceof Error ? error : undefined,
        completionError: status === 'success' ? result?.completionError : undefined,
      }
    },
  })

  return useMemo(
    () => mutations.filter((mutation): mutation is IssueDeletionState => !!mutation && mutation.id > initialMutationId),
    [initialMutationId, mutations],
  )
}
